import {TokenUsageInfoContent, useTokenUsageInfo} from '@github-ui/copilot-shared-ui/components/TokenUsageInfoContent'
import {sendEvent} from '@github-ui/hydro-analytics'
import {MeterIcon} from '@primer/octicons-react'
import {AnchoredOverlay, IconButton} from '@primer/react'
import {useState} from 'react'

export interface TokenUsageMeterSurfaceProps {
  /**
   * Usage accumulated by a live session, when there is one. A surface that has
   * not established a session yet passes `null` and shows entitlement alone.
   */
  sessionUsage: Parameters<typeof TokenUsageInfoContent>[0]['sessionUsage']
}

/**
 * The token meter itself: the trigger, its overlay, and the entitlement read
 * behind them.
 *
 * Session usage arrives as a value rather than being read here, because only an
 * established session has a timeline to read it from. That keeps one meter —
 * one placement, one label, one entitlement source — usable before a session
 * exists without giving the pre-session surface any live-session state.
 */
export function TokenUsageMeterSurface({sessionUsage}: TokenUsageMeterSurfaceProps) {
  const [isOpen, setIsOpen] = useState(false)
  const tokenUsageInfoData = useTokenUsageInfo({required: false})

  const hasUsageData = Boolean(tokenUsageInfoData) || Boolean(sessionUsage)
  if (!hasUsageData) return null

  return (
    <AnchoredOverlay
      open={isOpen}
      onOpen={() => {
        setIsOpen(true)
        sendEvent('copilot_chat.activate', {
          target: 'SESSION_TOKEN_USAGE_METER',
          action: 'opened',
          chat_version: 'v2',
          mode: 'immersive',
        })
      }}
      onClose={() => setIsOpen(false)}
      renderAnchor={props => (
        <IconButton
          icon={MeterIcon}
          {...props}
          type="button"
          aria-label="View token usage"
          aria-haspopup="dialog"
          variant="invisible"
          size="medium"
          data-testid="quick-chat-meter-button"
        />
      )}
      side="outside-top"
      align="center"
      anchorOffset={22}
      alignmentOffset={4}
      overlayProps={{role: 'dialog', 'aria-label': 'Token usage'}}
      variant={{regular: 'anchored', narrow: 'fullscreen'}}
      width="medium"
    >
      <TokenUsageInfoContent data={tokenUsageInfoData ?? null} sessionUsage={sessionUsage} />
    </AnchoredOverlay>
  )
}

TokenUsageMeterSurface.displayName = 'TokenUsageMeterSurface'

/**
 * The meter for a surface whose session does not exist yet.
 *
 * Only the entitlement half applies before establishment: there is no timeline
 * to have spent anything on. It is the same control in the same place, so the
 * remaining premium requests stay visible while composing the first turn.
 */
export function EntitlementTokenUsageMeter() {
  return <TokenUsageMeterSurface sessionUsage={null} />
}

EntitlementTokenUsageMeter.displayName = 'EntitlementTokenUsageMeter'
